import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Crown,
  CheckCircle2,
  AlertCircle,
  Clock,
  CreditCard,
  ShieldCheck,
  RefreshCw,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  ShieldAlert,
} from 'lucide-react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { CompanionMembershipPlan } from '../types';
import {
  createPaymentOrder,
  verifyPaymentWithBackend,
  checkPaymentStatus,
  StandardPaymentStatus,
} from '../lib/paymentService';
import { auth } from '../lib/firebase';
import { RazorpayCheckout } from '../components/RazorpayCheckout';

export const MembershipPage: React.FC = () => {
  const { userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();

  // Current view state in Membership Flow:
  // 'plan-selection' -> 'payment-page' -> automatic redirect to /payment-success
  const [currentView, setCurrentView] = useState<'plans' | 'payment' | 'failed' | 'pending'>('plans');

  const [selectedPlanId, setSelectedPlanId] = useState<CompanionMembershipPlan>(
    userProfile?.membership?.plan || 'PROFESSIONAL'
  );

  // Active Order Data from Backend
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [currentMembershipId, setCurrentMembershipId] = useState<string>('');
  const [checkoutKeyId, setCheckoutKeyId] = useState<string>('');
  const [checkoutAmountPaise, setCheckoutAmountPaise] = useState<number>(109900);
  const [orderAmount, setOrderAmount] = useState<number>(1099);
  const [orderPlanName, setOrderPlanName] = useState<string>('Professional Host');
  const [orderPlanDuration, setOrderPlanDuration] = useState<string>('3 Months');
  const [paymentStatus, setPaymentStatus] = useState<StandardPaymentStatus>('CREATED');

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isPaymentVerified = userProfile?.payment_verified === true;
  const isPaymentSuccess = userProfile?.payment_status === 'Success';
  const isProfileActive =
    userProfile?.companion_profile_status === 'Active' && isPaymentVerified && isPaymentSuccess;

  const plans = [
    {
      id: 'STARTER' as CompanionMembershipPlan,
      name: 'Starter Plan',
      price: 449,
      duration: '1 Month',
      durationLabel: '/ month',
      badge: 'Basic Tier',
      features: [
        'Standard profile listing in search',
        'Receive up to 5 client bookings/week',
        'Standard Escrow payout (24h)',
        'In-app verified chat messaging',
      ],
    },
    {
      id: 'PROFESSIONAL' as CompanionMembershipPlan,
      name: 'Professional Host',
      price: 1099,
      duration: '3 Months',
      durationLabel: '/ 3 months',
      badge: 'Most Popular',
      recommended: true,
      features: [
        'Priority search listing badge',
        'Unlimited bookings per week',
        'Instant OTP Escrow payout to bank',
        'Verified Companion Shield & KYC badge',
        'Direct phone & WhatsApp leads',
      ],
    },
    {
      id: 'PREMIUM' as CompanionMembershipPlan,
      name: 'Elite Celebrity Tier',
      price: 5999,
      duration: '1 Year',
      durationLabel: '/ year',
      badge: 'VIP Elite',
      features: [
        'Top of page VIP spotlight placement',
        '0% platform commission on meetups',
        'Dedicated 24/7 account manager',
        'VIP Events & High-Profile Dinners',
        'Maximum hourly rate flexibility',
      ],
    },
  ];

  // 1. User selects plan -> Proceeds to Payment Page -> Backend creates unique order
  const handleProceedToPayment = async (plan: typeof plans[0]) => {
    setIsProcessing(true);
    setErrorMessage(null);
    setSelectedPlanId(plan.id);
    setOrderAmount(plan.price);
    setOrderPlanName(plan.name);
    setOrderPlanDuration(plan.duration);

    try {
      const authUid = auth.currentUser?.uid;
      if (!authUid) {
        throw new Error('Please sign in with your verified account before starting membership payment.');
      }

      const orderRes = await createPaymentOrder({
        userId: authUid,
        companionId: authUid,
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        email: userProfile?.email,
        phone: userProfile?.phone,
        paymentMethod: 'Razorpay',
      });

      if (orderRes.success && orderRes.order_id && orderRes.keyId && Number(orderRes.amount) > 0) {
        setCurrentOrderId(orderRes.order_id);
        setCurrentMembershipId(orderRes.membership_id);
        setCheckoutKeyId(orderRes.keyId);
        setCheckoutAmountPaise(Number(orderRes.amount));
        setOrderAmount(Math.round(Number(orderRes.amount) / 100) || plan.price);
        setOrderPlanDuration(orderRes.plan_duration || plan.duration);
        setPaymentStatus(orderRes.status || 'CREATED');
        setCurrentView('payment');
      } else {
        throw new Error(orderRes.message || 'Failed to create backend payment order');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Network error while contacting gateway.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 2. Gateway Verification Logic (requires real Razorpay paymentId + signature)
  const handleVerify = async (paymentData: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) => {
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const authUid = auth.currentUser?.uid;
      if (!authUid) {
        throw new Error('Authenticated user session is required to verify payment.');
      }

      const res = await verifyPaymentWithBackend({
        orderId: paymentData.razorpay_order_id || currentOrderId,
        paymentId: paymentData.razorpay_payment_id,
        signature: paymentData.razorpay_signature,
        companionId: authUid,
        userId: authUid,
        planId: selectedPlanId,
        paymentMethod: 'Razorpay',
      });

      // RULE 2: PAYMENT SUCCESSFUL
      // Gateway verified SUCCESS/CAPTURED status:
      // Activate membership -> Save payment record in DB -> Automatically Next Page!
      if (
        res.success === true &&
        res.status === 'SUCCESS' &&
        res.payment_verified === true &&
        res.webhook_verified === 'YES'
      ) {
        setPaymentStatus('SUCCESS');

        await updateProfile({
          companion_profile_status: 'Active',
          membership_status: 'ACTIVE',
          payment_status: 'Success',
          payment_verified: true,
          membership: {
            plan: selectedPlanId,
            price: orderAmount,
            status: 'ACTIVE',
            nextBillingDate: 'Next Billing Cycle',
            paymentHistory: [
              {
                id: res.payment_id || paymentData.razorpay_payment_id,
                date: 'Today',
                amount: orderAmount,
                plan: selectedPlanId,
                status: 'PAID',
              },
              ...(userProfile?.membership?.paymentHistory || []),
            ],
          },
        });

        // Automatically Next Page: /payment-success -> /dashboard
        navigate('/payment-success', {
          replace: true,
          state: {
            orderId: res.order_id || currentOrderId,
            paymentId: res.payment_id || paymentData.razorpay_payment_id,
            membershipId: res.membership_id || currentMembershipId,
            planName: orderPlanName,
            amount: orderAmount,
            targetNext: '/dashboard',
          },
        });
        return;
      }

      // RULE 4: PAYMENT PENDING
      if (res.status === 'PENDING') {
        setPaymentStatus('PENDING');
        setCurrentView('pending');
        return;
      }

      // RULE 3: PAYMENT FAILED
      setPaymentStatus('FAILED');
      setErrorMessage(res.error || 'Your payment could not be completed.');
      setCurrentView('failed');
    } catch (err: any) {
      setPaymentStatus('FAILED');
      setErrorMessage(err.message || 'Payment verification failed.');
      setCurrentView('failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <DashboardLayout
      title="Companion Membership & Payment Gateway"
      subtitle="Select a verified membership plan to activate your Companion profile and receive bookings."
    >
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Active Profile Status Banner */}
        <div
          className={`p-5 rounded-3xl border transition-all ${
            isProfileActive
              ? 'bg-emerald-50/80 border-emerald-200 text-emerald-950'
              : 'bg-rose-50/80 border-rose-300 text-rose-950 shadow-2xs'
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={`w-3 h-3 rounded-full ${
                    isProfileActive ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'
                  }`}
                />
                <h3 className="text-sm font-black uppercase tracking-wide">
                  {isProfileActive ? 'Active Subscription' : 'Profile Inactive — Payment Required'}
                </h3>
              </div>
              <p className="text-xs opacity-90 max-w-2xl leading-relaxed">
                {isProfileActive
                  ? `Your ${userProfile?.membership?.plan || 'Professional'} membership is ACTIVE. You are visible in search and can receive client bookings.`
                  : 'Backend rule: Your companion profile status will remain INACTIVE until a successful payment is verified through the payment gateway.'}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span className="px-3.5 py-1.5 rounded-full text-xs font-black uppercase bg-white/90 shadow-2xs">
                {userProfile?.companion_profile_status || 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* VIEW 1: MEMBERSHIP PLAN SELECTION                                         */}
        {/* ========================================================================= */}
        {currentView === 'plans' && (
          <div className="space-y-6 animate-in fade-in">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-slate-900 tracking-tight">
                  Choose Your Membership Plan
                </h3>
                <p className="text-xs text-slate-500">
                  Select a tier to generate your secure backend order and proceed to payment.
                </p>
              </div>
              <span className="text-xs text-slate-500 font-semibold bg-white px-3 py-1 rounded-full border border-slate-200">
                Razorpay & UPI Auto-Verification
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {plans.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                const isCurrent = userProfile?.membership?.plan === plan.id && isProfileActive;

                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`p-6 rounded-3xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                      isSelected
                        ? 'border-purple-600 bg-white shadow-xl ring-2 ring-purple-500/20'
                        : 'border-slate-200 bg-white hover:border-slate-300 shadow-2xs'
                    }`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                            plan.recommended
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {plan.badge}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                            Current Plan
                          </span>
                        )}
                      </div>

                      <div>
                        <h4 className="text-lg font-black text-slate-900">{plan.name}</h4>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-3xl font-black text-slate-900">₹{plan.price}</span>
                          <span className="text-xs text-slate-400">{plan.durationLabel || '/ month'}</span>
                        </div>
                      </div>

                      <ul className="space-y-2 text-xs text-slate-600 pt-2 border-t border-slate-100">
                        {plan.features.map((feat, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-6 mt-4 border-t border-slate-100">
                      <button
                        type="button"
                        disabled={isProcessing}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProceedToPayment(plan);
                        }}
                        className={`w-full py-3 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-50 ${
                          plan.recommended
                            ? 'bg-gradient-to-r from-purple-700 to-indigo-800 hover:from-purple-800 hover:to-indigo-900 text-white shadow-purple-900/20'
                            : 'bg-slate-900 hover:bg-slate-800 text-white'
                        }`}
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>Proceed to Payment (₹{plan.price})</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 2: PAYMENT PAGE (REAL RAZORPAY CHECKOUT)                             */}
        {/* Shows: Membership name, duration, Amount, Order ID, Razorpay Checkout     */}
        {/* ========================================================================= */}
        {currentView === 'payment' && (
          <div className="space-y-6 max-w-xl mx-auto animate-in fade-in">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCurrentView('plans')}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" /> Change Membership Plan
              </button>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-purple-100 text-purple-800 border border-purple-200">
                Payment Verification Step
              </span>
            </div>

            {/* Payment Details Card */}
            <div className="bg-white rounded-3xl border border-purple-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-800 via-purple-700 to-indigo-800 text-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-purple-200 block">
                      Membership Plan
                    </span>
                    <h3 className="text-xl font-black">{orderPlanName}</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] uppercase font-bold text-purple-200 block">Amount</span>
                    <span className="text-2xl font-black">₹{orderAmount}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-purple-600/60 text-xs">
                  <div>
                    <span className="text-[10px] text-purple-300 block">Duration:</span>
                    <strong className="font-bold text-white">{orderPlanDuration}</strong>
                  </div>
                  <div>
                    <span className="text-[10px] text-purple-300 block">Order ID:</span>
                    <strong className="font-mono text-[11px] text-purple-100">{currentOrderId}</strong>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-purple-300 block">Payment Status:</span>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[10px] font-black">
                      {paymentStatus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Real Razorpay Checkout */}
              <div className="p-5 space-y-5">
                <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50 text-xs text-slate-700 space-y-2">
                  <div className="font-extrabold text-slate-900 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>Razorpay Checkout (UPI, Cards, NetBanking)</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Click below to open the real Razorpay payment window. Membership activates only after the backend verifies the Razorpay signature and captured payment status.
                  </p>
                </div>

                {errorMessage && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-700">
                    {errorMessage}
                  </div>
                )}

                <RazorpayCheckout
                  orderId={currentOrderId}
                  keyId={checkoutKeyId}
                  amountPaise={checkoutAmountPaise}
                  description={`${orderPlanName} membership`}
                  customerName={userProfile?.displayName || userProfile?.name}
                  customerEmail={userProfile?.email}
                  customerPhone={userProfile?.phone}
                  onPaymentResponse={handleVerify}
                  onDismiss={() => {
                    setErrorMessage('Checkout was closed. No payment success has been recorded.');
                  }}
                  onError={(message) => {
                    setPaymentStatus('FAILED');
                    setErrorMessage(message || 'Your payment could not be completed.');
                    setCurrentView('failed');
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 3: PAYMENT FAILED (RULE 3 OF SPECIFICATION)                          */}
        {/* ✕ Payment Failed. Your payment could not be completed. [Try Again]        */}
        {/* Next page-এ যাবে না (User stays on page, retry allowed)                   */}
        {/* ========================================================================= */}
        {currentView === 'failed' && (
          <div className="max-w-md mx-auto bg-white rounded-3xl border border-rose-200 shadow-xl p-6 sm:p-8 text-center space-y-6 animate-in zoom-in-95">
            <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10" />
            </div>

            <div className="space-y-1">
              <h3 className="text-2xl font-black text-slate-900">✕ Payment Failed</h3>
              <p className="text-sm font-semibold text-rose-700">
                Your payment could not be completed.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {errorMessage || 'Payment was declined by your bank or the gateway.'}
              </p>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-left text-xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Order ID:</span>
                <span className="font-mono text-slate-800">{currentOrderId}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Status:</span>
                <span className="font-bold text-rose-600">FAILED</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Membership:</span>
                <span className="font-bold text-slate-700">INACTIVE</span>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={() => setCurrentView('payment')}
                className="w-full py-3 bg-gradient-to-r from-purple-700 to-indigo-800 hover:from-purple-800 hover:to-indigo-900 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </button>

              <button
                type="button"
                onClick={() => setCurrentView('plans')}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Change Plan
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 4: PAYMENT PENDING (RULE 4 OF SPECIFICATION)                         */}
        {/* ⏳ Payment Pending. We are verifying your payment.                        */}
        {/* Please do not make another payment. [Check Payment Status]                */}
        {/* Pending অবস্থায় membership activate করা যাবে না।                           */}
        {/* ========================================================================= */}
        {currentView === 'pending' && (
          <div className="max-w-md mx-auto bg-white rounded-3xl border border-amber-200 shadow-xl p-6 sm:p-8 text-center space-y-6 animate-in zoom-in-95">
            <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <Clock className="w-10 h-10 animate-pulse" />
            </div>

            <div className="space-y-1">
              <h3 className="text-2xl font-black text-slate-900">⏳ Payment Pending</h3>
              <p className="text-sm font-semibold text-amber-800">
                We are verifying your payment.
              </p>
              <p className="text-xs text-slate-500">
                Please do not make another payment.
              </p>
            </div>

            <div className="p-4 bg-amber-50/80 rounded-2xl border border-amber-200 text-left text-xs space-y-2">
              <p className="text-amber-900 text-[11px] leading-relaxed">
                <strong>Pending Rule:</strong> Membership cannot be activated while payment is in Pending status. Once your bank confirms the transfer, membership will activate automatically.
              </p>
              <div className="pt-2 border-t border-amber-200 flex items-center justify-between text-slate-600">
                <span>Order ID:</span>
                <span className="font-mono text-slate-800 font-semibold">{currentOrderId}</span>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <button
                type="button"
                disabled={isProcessing}
                onClick={async () => {
                  setIsProcessing(true);
                  const status = await checkPaymentStatus(currentOrderId);
                  setIsProcessing(false);
                  if (status && status.status === 'SUCCESS' && status.webhook_verified === 'YES') {
                    setPaymentStatus('SUCCESS');
                    await updateProfile({
                      companion_profile_status: 'Active',
                      membership_status: 'ACTIVE',
                      payment_status: 'Success',
                      payment_verified: true,
                    });
                    navigate('/payment-success', {
                      replace: true,
                      state: {
                        orderId: status.order_id || currentOrderId,
                        paymentId: status.payment_id,
                        membershipId: status.membership_id || currentMembershipId,
                        planName: status.plan_name || orderPlanName,
                        amount: status.amount || orderAmount,
                        targetNext: '/dashboard',
                      },
                    });
                  } else {
                    alert('Payment is still pending with gateway. Please wait.');
                  }
                }}
                className="w-full py-3.5 bg-gradient-to-r from-purple-700 to-indigo-800 hover:from-purple-800 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <RefreshCw className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
                <span>Check Payment Status</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
